import { Check, Copy, X } from "lucide-react";
import type * as React from "react";
import { useState } from "react";
import { toast } from "sonner";
import { ErrorState } from "@/components/shared/error-state";
import { LogLevelBadge } from "@/components/system-logs/LogLevelBadge";
import { LogStatusBadge } from "@/components/system-logs/LogStatusBadge";
import { Skeleton } from "@/components/ui/skeleton";
import { type SystemLogDetail, useSystemLog } from "@/hooks/use-system-logs";

interface LogDetailDrawerProps {
  id: string | null;
  open: boolean;
  onClose: () => void;
}

/**
 * Build a paste-ready, self-contained text block for a log entry: every field
 * plus the stack and full context JSON. Intended for a user to copy and hand to
 * support/engineering when reporting an issue — it carries the whole context.
 */
function buildCopyText(detail: SystemLogDetail): string {
  const line = (label: string, value: string | number | null | undefined) =>
    `${label.padEnd(11)} ${value ?? "—"}`;

  const parts: string[] = [
    "Nymbl Staff — Log Entry",
    "========================",
    line("Time:", `${formatFullDate(detail.eventAt)}  (${detail.eventAt})`),
    line("ID:", detail.id),
    line("Level:", detail.level),
    line("Category:", detail.category),
    line("Message:", detail.message),
    line("Method:", detail.method),
    line("Path:", detail.path),
    line("Status:", detail.status),
    line("Duration:", detail.durationMs != null ? `${detail.durationMs}ms` : null),
    line("Actor:", detail.actorUserId),
    line("Request ID:", detail.requestId),
    line("IP:", detail.ip),
    line("User Agent:", detail.userAgent),
    line("Error Name:", detail.errorName),
  ];

  if (detail.errorStack) {
    parts.push("", "Error Stack:", detail.errorStack);
  }

  parts.push("", "Context:", detail.context ? JSON.stringify(detail.context, null, 2) : "(none)");

  return parts.join("\n");
}

function DetailField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 text-[12px]">{value}</div>
    </div>
  );
}

function formatFullDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function LogDetailDrawer({ id, open, onClose }: LogDetailDrawerProps) {
  const { data: detail, isLoading, isError, error, refetch } = useSystemLog(id, open);
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    if (!detail) return;
    try {
      await navigator.clipboard.writeText(buildCopyText(detail));
      setCopied(true);
      toast.success("Log entry copied — paste it into a bug report.");
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy to clipboard.");
    }
  }

  if (!open) return null;

  return (
    <>
      <button
        type="button"
        aria-label="Close drawer"
        className="fixed inset-0 z-40 bg-black/40"
        onClick={onClose}
      />
      <aside className="fixed right-0 top-0 z-50 flex h-screen w-full max-w-[560px] flex-col border-l border-border bg-background shadow-[0_0_40px_rgba(0,24,71,0.18)]">
        <header className="flex items-start justify-between border-b border-border px-5 py-4">
          <div>
            <h2 className="font-display text-[16px] font-bold leading-tight">Log Entry</h2>
            {detail && (
              <div className="mt-0.5 text-[11px] text-muted-foreground">
                {formatFullDate(detail.eventAt)}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handleCopy}
              disabled={!detail}
              className="flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
              title="Copy this entry and all its context to the clipboard"
            >
              {copied ? (
                <Check className="h-3.5 w-3.5 text-success-700" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
              {copied ? "Copied" : "Copy"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {isLoading && (
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-8 w-full rounded-md" />
              ))}
            </div>
          )}

          {isError && (
            <ErrorState
              message={error instanceof Error ? error.message : "Failed to load log entry."}
              onRetry={() => refetch()}
            />
          )}

          {!isLoading && !isError && detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <DetailField label="Level" value={<LogLevelBadge level={detail.level} />} />
                <DetailField
                  label="Status"
                  value={detail.status != null ? <LogStatusBadge status={detail.status} /> : "—"}
                />
                <DetailField label="Category" value={detail.category} />
                <DetailField
                  label="Duration"
                  value={detail.durationMs != null ? `${detail.durationMs}ms` : "—"}
                />
                <DetailField label="Method" value={detail.method ?? "—"} />
                <DetailField
                  label="Path"
                  value={<span className="font-mono text-[11px]">{detail.path ?? "—"}</span>}
                />
                <DetailField label="Message" value={detail.message} />
                <DetailField label="Error Name" value={detail.errorName ?? "—"} />
                <DetailField label="Actor" value={detail.actorUserId ?? "—"} />
                <DetailField label="Request ID" value={detail.requestId ?? "—"} />
                <DetailField label="IP" value={detail.ip ?? "—"} />
                <DetailField label="User Agent" value={detail.userAgent ?? "—"} />
              </div>

              {detail.errorStack && (
                <section>
                  <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Error Stack
                  </div>
                  <pre className="max-h-64 overflow-auto rounded-md border border-border bg-muted/40 p-3 font-mono text-[11px] leading-relaxed text-foreground">
                    {detail.errorStack}
                  </pre>
                </section>
              )}

              <section>
                <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Context
                </div>
                {detail.context ? (
                  <pre className="max-h-64 overflow-auto rounded-md border border-border bg-muted/40 p-3 font-mono text-[11px] leading-relaxed text-foreground">
                    {JSON.stringify(detail.context, null, 2)}
                  </pre>
                ) : (
                  <p className="text-[11px] text-muted-foreground">No additional context.</p>
                )}
              </section>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
